import { useTranslation } from 'module/hooks/use-translation';
import { Button, Dialog } from '@rocketlane/ui';
import { ReactNode, useCallback, useState } from 'react';
import * as signalStyles from './use-delete-signal-confirm-modal.module.css';

interface DeleteSignalConfirmConfig {
  title?: ReactNode;
  content?: ReactNode;
  width?: number;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  isEnabled?: boolean;
  onDisable?: () => void;
}

interface DeleteSignalConfirmResult {
  open: () => void;
  dialog: ReactNode;
}

export const useDeleteSignalConfirmModal = (
  config: DeleteSignalConfirmConfig = {},
): DeleteSignalConfirmResult => {
  const [open, setOpen] = useState(false);
  const titleText = useTranslation(
    'settings.signal_configuration.delete.confirm.title',
  );
  const messageText = useTranslation(
    'settings.signal_configuration.delete.confirm.message',
  );
  const cancelText = useTranslation('cancel');
  const disableText = useTranslation(
    'settings.signal_configuration.delete.confirm.disable_button',
  );
  const deleteText = useTranslation(
    'settings.signal_configuration.delete.confirm.delete_button',
  );
  const openDialog = useCallback(() => setOpen(true), []);
  const closeDialog = useCallback(() => setOpen(false), []);

  const paragraphs = String(config.content ?? messageText)
    .split('\n\n')
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return {
    open: openDialog,
    dialog: (
      <Dialog
        open={open}
        onOpenChange={setOpen}
        onCancel={config.onCancel}
        width={config.width ?? 560}
        title={config.title ?? titleText}
        content={
          <div className={signalStyles.content}>
            {paragraphs.map((paragraph) => (
              <div key={paragraph}>{paragraph}</div>
            ))}
          </div>
        }
        footer={
          <div className={signalStyles.footer}>
            <div className={signalStyles.footer_left}>
              <Button
                variant="ghost"
                onClick={() => {
                  config.onCancel?.();
                  closeDialog();
                }}
              >
                {cancelText}
              </Button>
            </div>

            <div className={signalStyles.footer_right}>
              <Button
                variant="secondary"
                onClick={() => {
                  if (config.isEnabled !== false) {
                    config.onDisable?.();
                  }
                  closeDialog();
                }}
              >
                {disableText}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  config.onOk?.();
                  closeDialog();
                }}
              >
                {deleteText}
              </Button>
            </div>
          </div>
        }
      />
    ),
  };
};
